import {
  CALL_NOT_RECORDED_CONSENT_MISSING,
  CONSENT_WINDOW_SENDER_ID,
  consentNotRecordedCopy,
  parseConsentCallEndedNotice,
  parseConsentNotRecordedNotice,
  RECORDING_CONSENT_MISSING_END_REASON,
  type ConsentCallEndedNotice,
  type ConsentNotRecordedNotice,
} from '@shared/calls/recording-consent'

function payloadFields(
  payload: string | Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  let value: unknown = payload
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') return null
  return value as Record<string, unknown>
}

/**
 * The popup notice carried by a consent-window END, or null for any other
 * signal. Only servers from before the not-recorded change end the call; the
 * same END reaches both parties, so both derive the same notice and differ
 * only in the copy chosen for the viewer.
 */
export function consentWindowEndNotice(signal: {
  type: string
  callId: string
  payload?: string | Record<string, unknown> | null
}): ConsentCallEndedNotice | null {
  if (signal.type !== 'END') return null
  const fields = payloadFields(signal.payload)
  if (!fields || fields.reason !== RECORDING_CONSENT_MISSING_END_REASON) return null
  return parseConsentCallEndedNotice({ ...fields, callId: signal.callId })
}

/**
 * The server's "this call isn't being recorded" notice, or null for any other
 * signal. It is a CONSENT signal from the consent-window sender, which no
 * signed-in client can impersonate. The call keeps going.
 */
export function consentNotRecordedNotice(signal: {
  type: string
  callId: string
  senderId?: string | null
  payload?: string | Record<string, unknown> | null
}): ConsentNotRecordedNotice | null {
  if (signal.type !== 'CONSENT' || signal.senderId !== CONSENT_WINDOW_SENDER_ID) return null
  const fields = payloadFields(signal.payload)
  if (!fields || fields.notRecordedReason !== CALL_NOT_RECORDED_CONSENT_MISSING) return null
  return parseConsentNotRecordedNotice(fields, signal.callId)
}

export type ConsentNotRecordedPlan = {
  /** The passing in-call notice for this viewer (no dialog, no END). */
  message: string
  isUnconsentedViewer: boolean
  /** Withdraw the post-connect sheet: consenting now would not record this call. */
  withdrawPostConnectSheet: boolean
}

/**
 * What the viewer's client does with the not-recorded notice: null when it is
 * for another call, the call is already over, or this call's notice already
 * showed (the heartbeat reply and the relay both deliver it).
 */
export function planConsentNotRecordedNotice<P extends string>(input: {
  notice: ConsentNotRecordedNotice
  live: { callId: string; phase: P; recordingConsentOwedPostConnect?: boolean } | null | undefined
  viewerUserId: string | null | undefined
  alreadyShownForCallId: string | null
  isTerminalPhase: (phase: P) => boolean
}): ConsentNotRecordedPlan | null {
  const { notice, live } = input
  if (live?.callId !== notice.callId || input.isTerminalPhase(live.phase)) return null
  if (input.alreadyShownForCallId === notice.callId) return null
  const copy = consentNotRecordedCopy(notice, input.viewerUserId)
  return {
    message: copy.message,
    isUnconsentedViewer: copy.isUnconsentedViewer,
    withdrawPostConnectSheet: Boolean(live.recordingConsentOwedPostConnect),
  }
}

/**
 * Whether the in-call "Recording on" indicator may be shown. Never while the
 * member's consent is still owed (capture is deferred) or once the server has
 * said this call is not recorded.
 */
export function showsRecordingIndicator(
  session: { recordingConsentOwedPostConnect?: boolean; callNotRecorded?: boolean } | null | undefined,
): boolean {
  return Boolean(session) && !session?.recordingConsentOwedPostConnect && !session?.callNotRecorded
}

type SignalLike = Parameters<typeof consentWindowEndNotice>[0] & { senderId?: string | null }

/**
 * Routes the mutation reply to our own HEARTBEAT. The heartbeat that crosses
 * the 30-second deadline is answered with the server's consent outcome: the
 * not-recorded notice (the call goes on), or an END from an older server. The
 * server also relays it to both parties' subscriptions, but that relay is
 * best-effort, so the reply is the sender's guaranteed copy. Returns true only
 * when the reply ended the call. Otherwise the reply is a plain HEARTBEAT.
 */
export async function routeHeartbeatReply<S extends SignalLike>(
  reply: S | null | undefined,
  onConsentEnd: (signal: S, notice: ConsentCallEndedNotice) => void | Promise<void>,
  onNotRecorded?: (notice: ConsentNotRecordedNotice) => void,
): Promise<boolean> {
  if (!reply) return false
  const notRecorded = consentNotRecordedNotice(reply)
  if (notRecorded) {
    onNotRecorded?.(notRecorded)
    return false
  }
  const notice = consentWindowEndNotice(reply)
  if (!notice) return false
  await onConsentEnd(reply, notice)
  return true
}
